import { DialogueConfirmation } from '@/components/dialogue/dialogue-confirmation';
import { ChoixNoyauxAjout } from '@/components/noyaux/choix-noyaux-ajout';
import { RedirectionLogin } from '@/components/resultat/redirection-login';
import { SessionExpireeError } from '@/lib/api';
import { preparerRelance } from '@/lib/brouillon';
import { lireSession } from '@/lib/session';
import { inscrireSuivi } from '@/lib/suivi-traductions';
import { useTitrePage } from '@/lib/titre-page';
import { useInterruption } from '@/lib/use-interruption';
import { PageLayout } from '@pms-digital/design-system';
import type { NoyauCode, ResumeTraduction } from '@rosetta/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { AucunResultat } from './aucun-resultat';
import { CarteEchec } from './carte-echec';
import { CarteEnCours } from './carte-en-cours';
import { CarteNouveauTexte } from './carte-nouveau-texte';
import { CarteTraduction } from './carte-traduction';
import { ChargementHistorique } from './chargement-historique';
import { ecarterEchec, lireEchecsEcartes } from './echecs-ecartes';
import { EnteteHistorique } from './entete-historique';
import { ErreurHistorique } from './erreur-historique';
import { filtrerTraductions } from './filtrer-traductions';
import { HistoriqueVide } from './historique-vide';
import { type CarteHistorique, regrouperParDocument } from './regrouper-traductions';
import { CLE_HISTORIQUE, useHistorique } from './use-historique';

interface EcranHistoriqueProps {
  /** The search of the address (`?recherche=`), what the field starts with. */
  recherche: string;
  /** The noyaux of the filter of the address (`?noyaux=`). */
  noyaux: readonly NoyauCode[];
  /** The search changed: the route writes it in the address. */
  onRecherche: (recherche: string) => void;
  /** The filter changed: the route writes it in the address. */
  onNoyaux: (noyaux: readonly NoyauCode[]) => void;
}

/**
 * The history screen (H2, and H4 when there is nothing): the header and its toolbar fixed
 * over the grid that scrolls, whose first cell is always the dashed "nouveau texte" card
 * (also when a search or a filter finds nothing). The centred column (1160 px and a 16 px gutter)
 * is as tall as the room under the logo header of the shell: the header first, then the one
 * zone that scrolls, which holds the cards only (and the messages of an empty or fruitless
 * list). Its scrollbar is thus right beside the cards, starting level with the first row,
 * and neither the page nor the header scrolls. The search and the filter work on the list
 * already loaded.
 *
 * A failed translation has its own white card (H5, #5), and so has an interrupted one (H7,
 * #7), unless the user dismissed it or relaunched it from there: those identifiers are
 * remembered in this browser, per account (`echecs-ecartes.ts`). A running translation (H6, #7)
 * has "interrompre" on its card: it asks for a confirmation (W2) before anything is cancelled.
 * A blocked one (H8, #13) has "relancer" too, and a partial one opens its result like a finished
 * one.
 *
 * The search and the filter come from the address (B8, #13: "retour" finds them again): the
 * fields keep their own state, so that typing never waits for the router, and tell the route at
 * each change. The zone of the cards gets its position back the same way (the scroll restoration
 * of the router, `data-scroll-restoration-id`). Under 1 100 px of window the grid has two columns,
 * and the page keeps 16 px of gutter on each side (B9, #13).
 *
 * The translations of one text (one document) are one card (#11, `regrouper-traductions.ts`): a
 * translated text has "autre noyau" (H8), which opens the choice of the noyaux to add (N1); its
 * "traduire" launches a new translation of the same document, which the card shows "en cours"
 * (H9, "interrompre" stopping the add only) until it ends (H10, and the toast T1 of #5).
 */
export function EcranHistorique({
  recherche: rechercheAdresse,
  noyaux: noyauxAdresse,
  onRecherche,
  onNoyaux,
}: EcranHistoriqueProps) {
  useTitrePage('Historique');
  const requete = useHistorique();
  const [recherche, setRecherche] = useState(rechercheAdresse);
  const [noyaux, setNoyaux] = useState<readonly NoyauCode[]>(noyauxAdresse);
  const navigate = useNavigate();

  function chercher(suivante: string) {
    setRecherche(suivante);
    onRecherche(suivante);
  }

  function filtrer(suivants: NoyauCode[]) {
    setNoyaux(suivants);
    onNoyaux(suivants);
  }
  const utilisateurId = lireSession()?.utilisateur.id;
  const [ecartes, setEcartes] = useState<ReadonlySet<string>>(() =>
    utilisateurId === undefined ? new Set() : lireEchecsEcartes(utilisateurId),
  );
  // What the live region says: a dismissed failure, or what a search leaves (emptied with it).
  const [annonce, setAnnonce] = useState({ texte: '', recherche: false });
  // The card whose "interrompre" was clicked, waiting for the confirmation (W2).
  const [aInterrompre, setAInterrompre] = useState<ResumeTraduction | null>(null);
  // The card whose "autre noyau" was clicked: the choice of the noyaux to add is open (N1, #11).
  const [aCompleter, setACompleter] = useState<CarteHistorique | null>(null);
  // A translation whose card has just changed (interrupted, H7; an add launched or stopped, #11):
  // the keyboard goes to the first button of its new card once it is drawn.
  const [aFocaliser, setAFocaliser] = useState<string | null>(null);
  // The add just launched (#11): its own card (H9) is the one to wait for, not the one it leaves.
  const ajoutAttendu = useRef<string | null>(null);
  const interruption = useInterruption({ versHistorique: false });
  const client = useQueryClient();
  const { data: lues, error } = requete;
  // One card per document (#11). The failures and interruptions the user got rid of are not part
  // of the list any more (nor of "vide").
  const data = lues
    ? regrouperParDocument(lues).filter(
        (carte) =>
          !(
            (carte.statut === 'echouee' || carte.statut === 'interrompue') &&
            ecartes.has(carte.id)
          ),
      )
    : undefined;

  const vide = data !== undefined && data.length === 0;
  const visibles = data === undefined ? [] : filtrerTraductions(data, recherche, noyaux);
  const maintenant = new Date();
  const rechercheActive = recherche.trim() !== '' || noyaux.length > 0;
  const nombreVisibles = visibles.length;
  const donneesLues = data !== undefined;

  // What a search or a filter leaves is told to a screen reader, once the typing has paused
  // (S14, #5): the list changes under the user's fingers and nothing else says so. It goes
  // through the live region of the dismissed failures, and is emptied with the search. The sentence is not the visible one ("Aucune traduction ne correspond..."): the same words
  // twice in the page would be read twice.
  useEffect(() => {
    if (!rechercheActive || !donneesLues) {
      setAnnonce((courante) => (courante.recherche ? { texte: '', recherche: false } : courante));
      return;
    }
    const delai = setTimeout(() => {
      setAnnonce({
        texte:
          nombreVisibles === 0
            ? 'Aucune traduction affichée.'
            : `${nombreVisibles} traduction${nombreVisibles > 1 ? 's' : ''} affichée${nombreVisibles > 1 ? 's' : ''}.`,
        recherche: true,
      });
    }, 600);
    return () => clearTimeout(delai);
  }, [rechercheActive, donneesLues, nombreVisibles]);

  // The button that had the focus went away with its card: the keyboard goes to the first button
  // of the new card of that translation's document, once it is drawn in its new state ("relancer"
  // on H7; "autre noyau" once an add is stopped; "interrompre" on the card of an add just launched),
  // the toast or the live region having said what happened.
  useEffect(() => {
    if (aFocaliser === null || data === undefined) return;
    const carte = data.find((candidate) => candidate.lancements.includes(aFocaliser));
    if (carte === undefined) return;
    const pret =
      ajoutAttendu.current === aFocaliser ? carte.id === aFocaliser : carte.statut !== 'en_cours';
    if (!pret) return;
    document.querySelector<HTMLElement>(`[data-traduction="${carte.id}"] button`)?.focus();
    ajoutAttendu.current = null;
    setAFocaliser(null);
  }, [aFocaliser, data]);

  if (error instanceof SessionExpireeError) return <RedirectionLogin />;

  // Asked while the translation ended in the meantime: nothing left to interrupt, no dialog.
  const statutAInterrompre =
    aInterrompre === null
      ? undefined
      : data?.find((traduction) => traduction.id === aInterrompre.id)?.statut;
  const confirmationOuverte = statutAInterrompre === 'en_cours' || statutAInterrompre === 'bloquee';

  async function interrompre() {
    const cible = aInterrompre;
    setAInterrompre(null);
    if (cible === null) return;
    const issue = await interruption.interrompre(cible.id);
    if (issue === 'interrompue' || issue === 'incomplete') setAFocaliser(cible.id);
  }

  function ajoutLance(carte: CarteHistorique, ajout: string, noyaux: readonly NoyauCode[]) {
    setACompleter(null);
    // Followed like any translation (#5): the toast T1 says when it is done, wherever the user is.
    if (utilisateurId !== undefined) {
      inscrireSuivi({ id: ajout, titre: carte.titre ?? '', utilisateurId });
    }
    ajoutAttendu.current = ajout;
    setAFocaliser(ajout);
    setAnnonce({ texte: `La traduction vers ${noyaux.join(', ')} est lancée.`, recherche: false });
    void client.invalidateQueries({ queryKey: CLE_HISTORIQUE });
  }

  function toutAfficher() {
    chercher('');
    filtrer([]);
  }

  function relancer(traduction: ResumeTraduction) {
    preparerRelance(traduction);
    void navigate({ to: '/' });
  }

  function ecarter(traduction: ResumeTraduction, carte: HTMLElement | null) {
    // The button that had the focus goes away with its card: the focus goes to a neighbour.
    const voisine = carte?.closest('li');
    const prochaine = voisine?.nextElementSibling ?? voisine?.previousElementSibling;
    if (utilisateurId !== undefined) ecarterEchec(utilisateurId, traduction.id);
    setEcartes((courants) => new Set([...courants, traduction.id]));
    setAnnonce({
      texte: `La traduction ${traduction.titre === undefined ? '' : `« ${traduction.titre} » `}est écartée.`,
      recherche: false,
    });
    prochaine?.querySelector<HTMLElement>('a')?.focus();
  }

  function corps() {
    if (data === undefined) {
      return error === null ? (
        <ChargementHistorique />
      ) : (
        <ErreurHistorique
          onReessayer={() => {
            void requete.refetch();
          }}
        />
      );
    }
    return (
      <>
        <ul
          aria-label="vos traductions"
          className="grid grid-cols-2 gap-6 min-[1100px]:grid-cols-3"
        >
          <li className="flex">
            <CarteNouveauTexte />
          </li>
          {visibles.map((traduction) => (
            // The key is the first translation of the document: the card stays the same element
            // when a noyau is added to its text (#11), only its content changes.
            <li
              key={traduction.lancements.at(-1) ?? traduction.id}
              data-traduction={traduction.id}
              className="flex [&>article]:w-full"
            >
              {traduction.statut === 'echouee' || traduction.statut === 'interrompue' ? (
                <CarteEchec
                  traduction={traduction}
                  maintenant={maintenant}
                  onRelancer={() => relancer(traduction)}
                  onEcarter={(evenement) => ecarter(traduction, evenement.currentTarget)}
                />
              ) : traduction.statut === 'en_cours' || traduction.statut === 'bloquee' ? (
                <CarteEnCours
                  traduction={traduction}
                  maintenant={maintenant}
                  interruptionEnCours={interruption.enCours.has(traduction.id)}
                  onInterrompre={() => setAInterrompre(traduction)}
                  onRelancer={() => relancer(traduction)}
                  {...(traduction.noyauxAjoutes === undefined
                    ? {}
                    : { noyauxAjoutes: traduction.noyauxAjoutes })}
                  {...(traduction.lancementsTraduits === undefined
                    ? {}
                    : { lancementsTraduits: traduction.lancementsTraduits })}
                  {...(traduction.lancementsAjoutes === undefined
                    ? {}
                    : { lancementsAjoutes: traduction.lancementsAjoutes })}
                  {...(traduction.idResultat === undefined
                    ? {}
                    : { idResultat: traduction.idResultat })}
                />
              ) : (
                <CarteTraduction
                  traduction={traduction}
                  maintenant={maintenant}
                  onAutreNoyau={() => setACompleter(traduction)}
                  {...(traduction.lancementsTraduits === undefined
                    ? {}
                    : { lancementsTraduits: traduction.lancementsTraduits })}
                />
              )}
            </li>
          ))}
        </ul>
        {vide ? <HistoriqueVide /> : null}
        {!vide && visibles.length === 0 ? <AucunResultat onToutAfficher={toutAfficher} /> : null}
      </>
    );
  }

  return (
    <PageLayout width="default">
      <output className="sr-only">{annonce.texte}</output>
      {/* The column fills the height under the 56 px header, less the top and bottom margins of the
          page (24, 32 then 60 px each): only the zone of the cards scrolls. */}
      <div className="flex h-[calc(100vh-104px)] flex-col md:h-[calc(100vh-120px)] xl:h-[calc(100vh-176px)]">
        <EnteteHistorique
          recherche={recherche}
          onRecherche={chercher}
          noyaux={noyaux}
          onNoyaux={filtrer}
        />
        {/* 6 px of room on the top and the left (taken back from the margins, nothing moves): the
            scrolling zone clips what is outside it, and the focus outline of a card (3 px, 2 px
            away) would be cut off at the edge of the first row and the first column. */}
        <div
          data-scroll-restoration-id="historique-cartes"
          className="-ml-2 mt-4 min-h-0 grow overflow-y-auto pt-2 pr-4 pb-6 pl-2 [scrollbar-gutter:stable]"
        >
          {corps()}
        </div>
      </div>
      <DialogueConfirmation
        open={confirmationOuverte}
        title="Interrompre cette traduction ?"
        description="Le traitement s'arrête et ne pourra pas reprendre. Vous pourrez relancer la traduction depuis l'historique."
        cancelLabel="annuler"
        confirmLabel="interrompre"
        confirmDanger
        onCancel={() => setAInterrompre(null)}
        onConfirm={() => {
          void interrompre();
        }}
      />
      {aCompleter === null ? null : (
        <ChoixNoyauxAjout
          key={aCompleter.id}
          id={aCompleter.id}
          titre={aCompleter.titre}
          onFermer={() => setACompleter(null)}
          onLance={(ajout, noyaux) => ajoutLance(aCompleter, ajout, noyaux)}
        />
      )}
    </PageLayout>
  );
}
